/**
 * Hosts the connected Greenhouse setup sections in one dialog.
 *
 * 1. Build the feature-gated section list.
 * 2. Keep the requested visible section in local state.
 * 3. Render the selected setup panel beside the navigation list.
 *
 * Each panel owns its Save and Cancel actions. The next file in this flow is the selected panel.
 */

import React, { FC, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { useFeatureFlagBool } from '@modernloop/shared/feature-flag';
import { Box, List, ListItemButton, ListItemText, Stack } from '@mui/material';

import { GreenhouseSubdomain } from '../GreenhouseSubdomain';

import GreenhouseJobSyncScopePanel from './GreenhouseJobSyncScopePanel';
import GreenhouseSubdomainPanel from './GreenhouseSubdomainPanel';

/** Identifies one section in the connected Greenhouse setup dialog. */
export type GreenhouseSetupSection = 'SUBDOMAIN' | 'JOBS_TO_SYNC';

/** Props for the connected Greenhouse setup dialog. */
interface Props {
  /** Section selected when the dialog opens. */
  initialSection: GreenhouseSetupSection;
  /** Existing Greenhouse subdomain shown in the Subdomain section. */
  initialSubdomain?: string;
  /** Disables setup actions while the card is saving or disabled. */
  disabled?: boolean;
  /** Shows the subdomain mutation in progress. */
  isSubmitting?: boolean;
  /** Closes the dialog. */
  onClose: () => void;
  /** Saves the Greenhouse subdomain through the card's existing flow. */
  onSubdomainSubmit: (subdomain: GreenhouseSubdomain) => void;
}

/** One navigation item and its optional feature-gated state. */
interface SectionItem {
  key: GreenhouseSetupSection;
  label: string;
  hidden?: boolean;
}

/** Renders the connected Greenhouse setup dialog and its active section. */
const GreenhouseSetupDialog: FC<Props> = ({
  initialSection,
  initialSubdomain = '',
  disabled = false,
  isSubmitting = false,
  onClose,
  onSubdomainSubmit,
}) => {
  const { t } = useTranslation();
  const greenhouseJobSyncScopeEnabled = useFeatureFlagBool('org_greenhouse_job_sync_scope');
  // Product decision: Jobs to sync is the first item; Subdomain is rarely changed after connect.
  const sections: SectionItem[] = [
    {
      key: 'JOBS_TO_SYNC',
      label: t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseSetup.nav.jobsToSync'),
      hidden: !greenhouseJobSyncScopeEnabled,
    },
    {
      key: 'SUBDOMAIN',
      label: t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseSetup.nav.subdomain'),
    },
  ];
  const visibleSections = sections.filter((section) => !section.hidden);
  const initialVisibleSection = sections.some((section) => section.key === initialSection && !section.hidden)
    ? initialSection
    : 'SUBDOMAIN';
  const [selectedSection, setSelectedSection] = useState<GreenhouseSetupSection>(initialVisibleSection);

  return (
    <Dialog
      open
      onClose={onClose}
      enableEscapeClose
      maxWidth="md"
      title={t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseSetup.title')}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
        <Box sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}>
          <List disablePadding>
            {visibleSections.map((section) => (
              <ListItemButton
                key={section.key}
                selected={selectedSection === section.key}
                onClick={() => setSelectedSection(section.key)}
              >
                <ListItemText primary={section.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {selectedSection === 'SUBDOMAIN' ? (
            <GreenhouseSubdomainPanel
              initialSubdomain={initialSubdomain}
              isSubmitting={isSubmitting}
              onCancel={onClose}
              onSubmit={onSubdomainSubmit}
            />
          ) : (
            <GreenhouseJobSyncScopePanel disabled={disabled} onClose={onClose} />
          )}
        </Box>
      </Stack>
    </Dialog>
  );
};

export default GreenhouseSetupDialog;
